import { Component, Input, OnInit } from '@angular/core';

import { ButtonTypesEnum } from '../../enums/button-types.enum';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { HubspotFormService } from '../../../Misc/services/hubspot-form.service';
import { HubspotFormTypeEnum } from '../../../Misc/enums/hubspot-form-type.enum';
import { ConfirmDialogComponent } from '../../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';

/**
 * Collapsible upsell banner promoting the Agentic (Agility Agent) features to users who are not
 * provisioned for them. Collapsed by default; expanding reveals the detail copy and a "Get In Touch"
 * button that submits a HubSpot inquiry and shows a "Request Received" confirmation. An optional
 * "Learn More" button can be shown by passing a `learnMoreUrl`.
 *
 * Designed to be reused across the Agentic upsell placements (New Search, Edit Topic panel, Canvas,
 * Search Summary RHP) by overriding the heading/message inputs.
 */
@Component({
    selector: 'agpr-agentic-upsell-banner',
    templateUrl: './agentic-upsell-banner.component.html',
    styleUrls: ['./agentic-upsell-banner.component.scss'],
    standalone: false
})
export class AgenticUpsellBannerComponent implements OnInit {
    readonly buttonTypes = ButtonTypesEnum;

    @Input() id = 'agenticUpsellBanner';
    @Input() heading = 'Common.agenticUpsell.search.heading';
    @Input() message = 'Common.agenticUpsell.search.message';
    // When set, a secondary "Learn More" button is shown that opens this URL in a new tab.
    @Input() learnMoreUrl?: string;

    // Drives the expanded chrome (box-shadow, outer border, square titlebar bottom). Kept true for the
    // whole collapse animation and only flipped false once the panel finishes animating, so the bottom
    // border slides up smoothly to meet the label instead of jumping there at the start of the toggle.
    isExpanded: boolean = false;
    isWhiteLabeledBrand: boolean = false;

    constructor(
        protected configService: ConfigService,
        protected hubspotFormService: HubspotFormService,
        protected coreDialogService: CoreDialogService
    ) {}

    ngOnInit() {
        this.isWhiteLabeledBrand = this.configService.isWhiteLabelBrand();
    }

    /**
     * Fired immediately on toggle. Apply the expanded chrome right away when opening so the card
     * grows with its border/shadow; when closing, leave it until the animation completes.
     */
    onCollapsedChange(collapsed: boolean): void {
        if (!collapsed) {
            this.isExpanded = true;
        }
    }

    /**
     * Fired when the collapse/expand animation finishes. Once collapsed, revert to the collapsed
     * chrome — by now the content height is 0 so the swap is seamless.
     */
    onAfterToggle(event: { collapsed: boolean }): void {
        if (event.collapsed) {
            this.isExpanded = false;
        }
    }

    learnMore(): void {
        window.open(this.learnMoreUrl, '_blank');
    }

    getInTouch(): void {
        if (this.configService.brandGUIDIsBurrelles()) {
            window.location.href = 'mailto:inquiry@burrelles.com?subject=Sales%20Inquiry%20-%20Burrelles%20Contact%20Database';
        } else if (this.configService.brandGUIDIsPaMedia()) {
            window.location.href = 'mailto:salesadminteam@pamediagroup.com?'+
            'subject=Urgent%3A%20Agility%20Feature%20Upgrade%20Request&'+
            'body=Hello%2C%0A%0AI%27m%20interested%20in%20upgrading%20to%20include%20the%20%22PR%20CoPilot%22%20feature%20in%20my%20plan.';
        } else {
            this.hubspotFormService.submitForm(HubspotFormTypeEnum.AGENTIC_FEATURES).then(() => {
                this.coreDialogService.open(
                    ConfirmDialogComponent,
                    {
                        inputs: {
                            heading: 'Common.agenticUpsell.confirmation.header',
                            messageHtml: 'Common.agenticUpsell.confirmation.message',
                            cancelLabel: 'cancel',
                            width: 600
                        }
                    }
                );
            }).catch(err => {
                window.open('https://www.agilitypr.com/contact/');
            });
        }
    }
}
